import React from "react";
import { useSpreadsheetViewer } from "../context/SpreadsheetViewerContext";
import type { CitedCellValue, ExcelCellCitation } from "../types/citations";
import { openDopplerUri } from "../utils/dopplerUri";

interface CitedExcelCellProps {
	cell: CitedCellValue;
	chatId?: string | null;
	className?: string;
	style?: React.CSSProperties;
	fallback?: React.ReactNode;
}

export const isCitedCellValue = (value: unknown): value is CitedCellValue => {
	if (!value || typeof value !== "object") return false;
	const rec = value as Record<string, unknown>;
	return Array.isArray(rec.citations);
};

export const displayValueForCitedCell = (cell: CitedCellValue): string => {
	if (cell.display_value !== undefined && cell.display_value !== null) {
		return String(cell.display_value);
	}
	if (cell.value !== undefined && cell.value !== null) {
		return String(cell.value);
	}
	return "";
};

const excelCitationHref = (citation: ExcelCellCitation): string | null => {
	if (!Number.isInteger(citation.file_id) || citation.file_id <= 0) return null;
	const sheet = citation.sheet?.trim();
	const ref = citation.ref || citation.cell || citation.range;
	if (!sheet || !ref) return null;
	const params = new URLSearchParams();
	params.set("sheet", sheet);
	if (String(ref).includes(":") || citation.range) {
		params.set("range", String(ref));
	} else {
		params.set("cell", String(ref));
	}
	if (citation.filename) {
		params.set("file", citation.filename);
	}
	return `doppler://excel/${citation.file_id}?${params.toString()}`;
};

const CitedExcelCell: React.FC<CitedExcelCellProps> = ({
	cell,
	chatId,
	className,
	style,
	fallback,
}) => {
	const spreadsheetViewer = useSpreadsheetViewer();
	const citation = cell.citations?.[0];
	const href = citation ? excelCitationHref(citation) : null;
	const label = displayValueForCitedCell(cell);
	const ref = citation?.ref || citation?.cell || citation?.range;
	const title =
		citation && ref
			? `${citation.filename ? `${citation.filename} - ` : ""}${
					citation.sheet
				}!${ref}`
			: undefined;

	if (!href) {
		return <>{fallback ?? label}</>;
	}

	const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		const opened =
			spreadsheetViewer?.openSpreadsheetHref(href, {
				chatId,
				filename: citation?.filename,
			}) ?? false;
		if (!opened) void openDopplerUri(href, { chatId });
	};

	return (
		<a
			href={href}
			onClick={handleClick}
			className={className ?? "markdown-doppler-link"}
			style={style}
			title={title}
		>
			{label}
		</a>
	);
};

export default CitedExcelCell;
